import { useState } from 'react';
import { X, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';

export default function MergeProductModal({ product, products, onClose, onDone }) {
  const candidates = products.filter((p) => p.product_id !== product.product_id && !!p.is_service === !!product.is_service);
  const [targetId, setTargetId] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const target = candidates.find((p) => p.product_id === targetId);

  const pick = (id) => {
    setTargetId(id);
    setName(candidates.find((p) => p.product_id === id)?.name || '');
  };

  const submit = async () => {
    if (!target) return toast.error('Choose a product to merge into');
    if (!window.confirm(`Merge "${product.name}" into "${target.name}"? "${product.name}" will be removed. This cannot be undone.`)) return;
    setSaving(true);
    try {
      await adminApi.post(`/admin/products/${product.product_id}/merge`, { target_id: targetId, new_name: name });
      toast.success(`Merged into ${name || target.name}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="merge-product-modal">
        <div className="p-6 border-b border-[#e7e7e7] flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2"><GitMerge size={18} /> Merge product</h2>
            <p className="text-sm text-[#6b7280]">Move all history of <b>{product.name}</b> into another product</p>
          </div>
          <button onClick={onClose} data-testid="close-merge-modal"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">Merge into</div>
            <select className="mrg-input" value={targetId} onChange={(e) => pick(e.target.value)} data-testid="merge-target-select">
              <option value="">Select product…</option>
              {candidates.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({p.remaining_qty} on hand)</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">Name after merge</div>
            <input className="mrg-input" value={name} onChange={(e) => setName(e.target.value)} disabled={!target} data-testid="merge-name-input" />
          </label>
          <p className="text-xs text-[#6b7280]">Invoices, orders, customer prices, hidden-product settings and stock batches move to the merged product. Past invoice costs and profit stay unchanged.</p>
        </div>
        <div className="p-6 border-t border-[#e7e7e7] flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={saving || !target} className="btn-brand" data-testid="merge-submit-btn">{saving ? 'Merging…' : 'Merge'}</button>
        </div>
      </div>
      <style>{`.mrg-input{width:100%;padding:9px 12px;border:1px solid #e7e7e7;border-radius:8px;font-size:14px;background:#fff;} .mrg-input:focus{outline:none;border-color:#d92027;box-shadow:0 0 0 2px #fde8e8;}`}</style>
    </div>
  );
}
